import { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DraggableList, TextInput } from "../shared";
import { MarkdownContent } from "@/components/ui/markdown-content";
import { cn } from "@/lib/utils";
import type { OrderingSpec, OrderingAnswer } from "@/types/hld-wizard";

const fadeIn = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.3 },
};

interface OrderingDecisionProps {
  spec: OrderingSpec;
  context: string;
  question: string;
  answer: OrderingAnswer | undefined;
  onChange: (answer: OrderingAnswer) => void;
  disabled?: boolean;
}

function shuffleArray<T>(arr: T[]): T[] {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function OrderingDecision({
  spec,
  context,
  question,
  answer,
  onChange,
  disabled,
}: OrderingDecisionProps) {
  // Stable initial shuffle — only on first mount when no answer exists
  const initialOrderRef = useRef<string[] | null>(null);
  if (!initialOrderRef.current) {
    if (answer?.initial_order?.length) {
      // Restore from draft/existing answer
      initialOrderRef.current = answer.initial_order;
    } else {
      initialOrderRef.current = shuffleArray(spec.items.map((i) => i.id));
    }
  }

  const currentOrder = answer?.order ?? initialOrderRef.current;

  function handleReorder(newOrder: string[]) {
    onChange({
      order: newOrder,
      initial_order: initialOrderRef.current!,
      followup_option_id: answer?.followup_option_id ?? "",
      followup_justify_text: answer?.followup_justify_text ?? "",
    });
  }

  function selectFollowup(optionId: string) {
    onChange({
      order: currentOrder,
      initial_order: initialOrderRef.current!,
      followup_option_id: optionId,
      followup_justify_text: answer?.followup_justify_text ?? "",
    });
  }

  function setFollowupJustify(text: string) {
    if (!answer) return;
    onChange({ ...answer, followup_justify_text: text });
  }

  return (
    <div className="space-y-6">
      {context && (
        <div className="border-l-2 border-primary/30 pl-4 text-muted-foreground">
          <MarkdownContent>{context}</MarkdownContent>
        </div>
      )}

      <MarkdownContent className="font-medium">{question}</MarkdownContent>

      <DraggableList
        items={spec.items}
        order={currentOrder}
        onReorder={handleReorder}
        disabled={disabled}
      />

      {/* Followup question — always visible (not gated) */}
      <div className="space-y-4">
        <p className="font-medium">{spec.followup.question}</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {spec.followup.options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => selectFollowup(opt.id)}
              disabled={disabled}
              className={cn(
                "rounded-lg border p-3 text-left text-sm transition-all",
                answer?.followup_option_id === opt.id
                  ? "border-primary bg-primary/5 ring-1 ring-primary/20 font-medium"
                  : "border-border hover:border-primary/40",
                disabled && "cursor-not-allowed opacity-50",
              )}
            >
              {opt.text}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {answer?.followup_option_id && (
          <motion.div {...fadeIn}>
            <TextInput
              value={answer.followup_justify_text}
              onChange={setFollowupJustify}
              placeholder={spec.followup_justify.placeholder}
              maxChars={spec.followup_justify.max_chars}
              disabled={disabled}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
